import React, { useEffect, useState } from 'react';
import { IParentScreenContent, IChildScreenReference, IAppScreen, ScreenContentUtils } from '../MobileAppTypes';
import { IScreenEditorProps } from './SharedTypes';
import { EasyFileInput } from '../../Tools/ExpandableDiv';
import Button from '../../_Core/Button';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import Editor from '../../GenericForms/Editor';

interface IParentScreenEditorProps extends IScreenEditorProps {
  content: IParentScreenContent;
  availableScreens?: IAppScreen[];
}

const ParentScreenEditor: React.FC<IParentScreenEditorProps> = ({ content, errors, onContentChange, onFilesChange, onValidationChange, availableScreens = [] }) => {
  const [localContent, setLocalContent] = useState<IParentScreenContent>(content);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [localChildScreens, setLocalChildScreens] = useState<IChildScreenReference[]>([]);

  useEffect(() => {
    setLocalContent(content);
    if (content.childScreens_string) {
      try {
        const parsedChildScreens: IChildScreenReference[] = ScreenContentUtils.fromBase64ToObj(content.childScreens_string);
        setLocalChildScreens(parsedChildScreens);
      } catch (error) {
        console.error('Failed to parse child screens JSON:', error);
        setLocalChildScreens([]);
      }
    } else {
      setLocalChildScreens([]);
    }
  }, [content]);

  const updateContent = (updates: Partial<IParentScreenContent>, updatedChildScreens?: IChildScreenReference[]) => {
    const newContent = { ...localContent, ...updates };

    if (updatedChildScreens) {
      setLocalChildScreens(updatedChildScreens);
      newContent.childScreens_string = ScreenContentUtils.objToBase64JsonString(updatedChildScreens);
      newContent.childScreenLength = updatedChildScreens.length;
    }

    setLocalContent(newContent);
    onContentChange(newContent);
    // Validate on change
    const validationErrors = validate(newContent);
    onValidationChange(validationErrors);
  };

  const validate = (contentToValidate: IParentScreenContent = localContent): string[] => {
    const errors: string[] = [];
    if (!contentToValidate.title) errors.push('Page title is required');
    if (!contentToValidate.displayMode) errors.push('Display mode is required');

    return errors;
  };

  const addChildScreen = () => {
    const newChild: IChildScreenReference = {
      screenId: '',
      displayTitle: '',
      summary: '',
      iconUrl: '',
      order: localContent.childScreenLength || 0,
    };

    const newChildScreens = [...localChildScreens, newChild];
    updateContent({}, newChildScreens);
  };

  const removeChildScreen = (index: number) => {
    const newChildScreens = localChildScreens.filter((_, i) => i !== index);
    // Update order for remaining items
    const reorderedScreens = newChildScreens.map((child, i) => ({ ...child, order: i }));
    updateContent({}, reorderedScreens);
  };

  const updateChildScreen = (index: number, updates: Partial<IChildScreenReference>) => {
    const newChildScreens = localChildScreens.map((child, i) => (i === index ? { ...child, ...updates } : child));
    updateContent({}, newChildScreens);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      return;
    }

    const newChildScreens = [...localChildScreens];
    const draggedScreen = newChildScreens[draggedIndex];
    newChildScreens.splice(draggedIndex, 1);
    newChildScreens.splice(targetIndex, 0, draggedScreen);

    // Update order for all items
    const reorderedScreens = newChildScreens.map((child, i) => ({ ...child, order: i }));
    updateContent({}, reorderedScreens);
    setDraggedIndex(null);
  };

  const getScreenTitle = (screenId: string): string => {
    const screen = availableScreens.find((s) => s.PublicId === screenId);
    return screen ? screen.MenuTitle || screen.Title : 'Unknown Screen';
  };

  const isDisplayModeWithOptions = (mode: string): boolean => {
    return mode === 'List' || mode === 'Cards';
  };

  return (
    <div className="form-section">
      <Tabs>
        <TabList>
          <Tab>Settings</Tab>
          <Tab>Child Screens</Tab>
        </TabList>
        <TabPanel>
          <h4>Parent Screen Content</h4>
          <div className="form-horizontal">
            {/* Basic Settings */}
            <div className="form-group">
              <label className="col-sm-3 control-label">Page Title*</label>
              <div className="col-sm-9">
                <input type="text" className="form-control" value={localContent.title} onChange={(e) => updateContent({ title: e.target.value })} placeholder="Parent screen title" />
              </div>
            </div>

            <div className="form-group">
              <label className="col-sm-3 control-label">Description</label>
              <div className="col-sm-9">
                <textarea
                  className="form-control"
                  rows={2}
                  value={localContent.description || ''}
                  onChange={(e) => updateContent({ description: e.target.value })}
                  placeholder="Optional description for the parent screen"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="col-sm-3 control-label">Display Mode*</label>
              <div className="col-sm-9">
                <select
                  className="form-control"
                  value={localContent.displayMode}
                  onChange={(e) =>
                    updateContent({
                      displayMode: e.target.value as 'List' | 'Dropdown' | 'Cards',
                    })
                  }>
                  <option value="List">List</option>
                  <option value="Dropdown">Dropdown</option>
                  <option value="Cards">Cards</option>
                </select>
                <small className="help-block">List and Cards modes support additional display options like summaries and icons</small>
              </div>
            </div>

            {/* Display Options - Only for List and Cards modes */}
            {isDisplayModeWithOptions(localContent.displayMode) && (
              <>
                <div className="form-group">
                  <label className="col-sm-3 control-label">Show Summaries</label>
                  <div className="col-sm-9">
                    <input type="checkbox" checked={localContent.showSummary === "true"} onChange={(e) => updateContent({ showSummary: e.target.checked ? "true" : "false" })} />
                    <span className="ml-2">Display summary/description for each child screen</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Show Icons</label>
                  <div className="col-sm-9">
                    <input type="checkbox" checked={localContent.showIcons === "true"} onChange={(e) => updateContent({ showIcons: e.target.checked ? "true" : "false" })} />
                    <span className="ml-2">Display icons for each child screen</span>
                  </div>
                </div>
              </>
            )}

            <div className="form-group">
              <label className="col-sm-3 control-label">Header Content</label>
              <div className="col-sm-9">
                <Editor
                  options="simple"
                  height="normal"
                  value={localContent.headerContent || ''}
                  changed={(content: string) => updateContent({ headerContent: content })}
                  readOnly={false}
                  placeholder="Enter your formatted content here..."
                  hideAlign
                />
              </div>
            </div>
          </div>
        </TabPanel>
        <TabPanel>
          <div className="form-horizontal">
            {/* Child Screens Management */}
            <div className="form-group">
              <label className="col-sm-3 control-label">Child Screens*</label>
              <div className="col-sm-9">
                <Button type="add" text="Add Child Screen" onClick={addChildScreen} disabled={availableScreens.length === 0} />
                {availableScreens.length === 0 && <small className="help-block text-muted">No screens available. Create other screens first before adding them as children.</small>}
              </div>
            </div>

            {/* Child Screens List */}
            {localChildScreens.map((childScreen, index) => (
              <div
                key={index}
                className="form-group child-screen-item"
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                style={{
                  border: '1px solid #ddd',
                  padding: '15px',
                  marginBottom: '10px',
                  borderRadius: '5px',
                  backgroundColor: draggedIndex === index ? '#f0f0f0' : '#fff',
                  cursor: 'move',
                }}>
                <div className="row">
                  <div className="col-sm-1">
                    <span className="drag-handle" style={{ fontSize: '18px', color: '#ccc' }}>
                      ⋮⋮
                    </span>
                    <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>#{index + 1}</div>
                  </div>

                  <div className="col-sm-11">
                    <div className="row">
                      <div className="col-sm-6">
                        <label>Select Screen*</label>
                        <select className="form-control" value={childScreen.screenId} onChange={(e) => updateChildScreen(index, { screenId: e.target.value })}>
                          <option value="">-- Select a screen --</option>
                          {availableScreens.map((screen) => (
                            <option key={screen.PublicId} value={screen.PublicId}>
                              {screen.MenuTitle || screen.Title}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-sm-6">
                        <label>Display Title Override</label>
                        <input
                          type="text"
                          className="form-control"
                          value={childScreen.displayTitle || ''}
                          onChange={(e) => updateChildScreen(index, { displayTitle: e.target.value })}
                          placeholder={`Default: ${getScreenTitle(childScreen.screenId)}`}
                        />
                      </div>
                    </div>

                    {/* Additional fields for List/Cards modes */}
                    {isDisplayModeWithOptions(localContent.displayMode) && (
                      <div className="row" style={{ marginTop: '10px' }}>
                        <div className="col-sm-6">
                          <label>Summary {localContent.showSummary ? '*' : '(optional)'}</label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={childScreen.summary || ''}
                            onChange={(e) => updateChildScreen(index, { summary: e.target.value })}
                            placeholder="Brief description of this screen"
                            disabled={!localContent.showSummary}
                          />
                        </div>

                        <div className="col-sm-6">
                          <label>Icon {localContent.showIcons ? '*' : '(optional)'}</label>
                          {localContent.showIcons ? (
                            <EasyFileInput
                              htmlId={`child-icon-${index}`}
                              exts=".jpg,.jpeg,.png,.gif,.svg,.webp,.heic"
                              fileSize={5}
                              value={childScreen.iconUrl || ''}
                              onSelect={(url: string) => updateChildScreen(index, { iconUrl: url })}
                              onSelectFile={(file: File) => {
                                const fileKey = `child_screen_icon_${index}`;
                                onFilesChange({ [fileKey]: file });
                              }}
                            />
                          ) : (
                            <div className="form-control-static text-muted">Icon upload is disabled when "Show Icons" is unchecked</div>
                          )}
                        </div>
                      </div>
                    )}

                    <div style={{ marginTop: '10px', textAlign: 'right' }}>
                      <Button type="remove" text="Remove" onClick={() => removeChildScreen(index)} />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {localChildScreens.length === 0 && (
              <div className="form-group">
                <div className="col-sm-offset-3 col-sm-9">
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666', border: '2px dashed #ddd', borderRadius: '5px' }}>
                    <p>No child screens added yet.</p>
                    <p>Click "Add Child Screen" to get started.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
};

export default ParentScreenEditor;
